// Single source of truth for dark/light mode across the storefront and every
// dashboard. Historically two keys were used independently:
//   - 'theme'           -> 'dark' | 'light'   (storefront: App, ProductDetails, …)
//   - 'autoboyDarkMode' -> 'true' | 'false'   (dashboards, Navbar, FloatingButtons)
// They drifted out of sync. This module keeps BOTH keys written together so all
// existing readers stay correct, treats 'theme' as canonical, and emits an event
// so always-mounted components (e.g. App) can re-sync when the value changes.

export const THEME_EVENT = 'autoboy-theme-change';

// Read the current preference. 'theme' wins; falls back to the legacy
// 'autoboyDarkMode' key, then to the OS preference.
export const getStoredDarkMode = () => {
  try {
    const t = localStorage.getItem('theme');
    if (t === 'dark') return true;
    if (t === 'light') return false;
    const legacy = localStorage.getItem('autoboyDarkMode');
    if (legacy != null) return legacy === 'true';
  } catch (_) { /* ignore */ }
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
};

// Persist the preference to BOTH keys and notify listeners in the same tab.
export const setStoredDarkMode = (isDark) => {
  try {
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    localStorage.setItem('autoboyDarkMode', JSON.stringify(!!isDark));
  } catch (_) { /* ignore */ }
  try {
    window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: { isDark: !!isDark } }));
  } catch (_) { /* ignore */ }
};

// Subscribe to theme changes (same-tab custom event + cross-tab storage event).
// Returns an unsubscribe function.
export const onThemeChange = (handler) => {
  const evt = () => handler(getStoredDarkMode());
  const storage = (e) => {
    if (!e || e.key === 'theme' || e.key === 'autoboyDarkMode') handler(getStoredDarkMode());
  };
  window.addEventListener(THEME_EVENT, evt);
  window.addEventListener('storage', storage);
  return () => {
    window.removeEventListener(THEME_EVENT, evt);
    window.removeEventListener('storage', storage);
  };
};
